import { useCallback, useEffect, useMemo } from 'react';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import { getColumnsForSession } from './columns/columns';
import { useTableData } from './use-table-data';
import { useTableFilters } from '../filters/use-table-filters';
import { useTableSort } from '../sort/use-table-sort';
import { usePagination } from '../pagination/use-pagination';
import { useResponsiveColumns } from './use-responsive-columns';
import { useNavigate } from '../../../../../infra/routing';
import { useSession } from '../../../../../infra/session';
import { buildCampaignUrlFromList } from '../../../utils/url-builders';
var getEmptyTableState = function (count, filterState) {
    if (count === 0) {
        if (filterState.searchFilter || filterState.typeFilter !== 'all') {
            return 'search';
        }
        if (filterState.statusFilter !== 'all') {
            return filterState.statusFilter;
        }
    }
    return null;
};
export var useEngageTable = function () {
    var _a = useTableFilters(), filterState = _a.filterState, setStatusFilter = _a.setStatusFilter, setTypeFilter = _a.setTypeFilter, setCreatorFilter = _a.setCreatorFilter;
    var paginationState = usePagination(filterState);
    var sortState = useTableSort();
    var _b = useTableData({
        pagination: paginationState.pagination,
        filterState: filterState,
        sortState: sortState,
    }), tableData = _b.tableData, isLoadingTableData = _b.isLoadingTableData, count = _b.count, totalPages = _b.totalPages;
    var session = useSession().data;
    var sessionColumns = useMemo(function () { return getColumnsForSession(session); }, [session]);
    var columnVisibilityState = useResponsiveColumns(sessionColumns);
    var table = useReactTable({
        data: tableData,
        columns: sessionColumns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination: paginationState.pagination,
            columnVisibility: columnVisibilityState.columnVisibility,
        },
        autoResetPageIndex: false,
        paginateExpandedRows: false,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        pageCount: totalPages,
    });
    useEffect(function () {
        if (!columnVisibilityState.hasExpandingColumns) {
            table.resetExpanded(false);
        }
    }, [columnVisibilityState.hasExpandingColumns]);
    var navigate = useNavigate();
    var onRowClick = useCallback(function (row) {
        if (columnVisibilityState.hasExpandingColumns) {
            row.toggleExpanded();
        }
        else {
            navigate(buildCampaignUrlFromList({ campaign: row.original, session: session }));
        }
    }, [columnVisibilityState.hasExpandingColumns, session]);
    return {
        table: table,
        isLoading: isLoadingTableData,
        emptyTableState: getEmptyTableState(count, filterState),
        onRowClick: onRowClick,
        filters: {
            filterState: filterState,
            setStatusFilter: setStatusFilter,
            setTypeFilter: setTypeFilter,
            setCreatorFilter: setCreatorFilter,
        },
        sort: sortState,
        pagination: paginationState,
        count: count,
    };
};
